import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { addHreflangLinks, getCanonicalUrl } from '@/lib/urlHelpers';
import { ArrowLeft, MapPin, ExternalLink, Play, Heart, RefreshCw, Phone, Building2, X, Headphones } from 'lucide-react';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/hooks/useAuth';
import { PlaceDetailCard, RatingDisplay } from '@/components/PlaceDetailCard';
import { PlaceAudioSection } from '@/components/PlaceAudioSection';
import { OptimizedImage } from '@/components/OptimizedImage';
import { NearbyPlaces } from '@/components/NearbyPlaces';
import { PodcastMenuButton } from '@/components/PodcastMenuButton';
import { useNavigationState } from '@/hooks/useNavigationState';
import PlaceDetailSkeleton from '@/components/PlaceDetailSkeleton';
import PodcastPlayer, { type PodcastPlayerHandle } from '@/components/PodcastPlayer';
import { usePlaceDetailQuery, placeDetailQueryKey } from '@/hooks/usePlaceDetailQuery';
import type { PlaceData } from '@/types/placeDetail';

const VisitTipsSection = lazy(() => import('@/components/VisitTipsSection'));
const PodcastStorySection = lazy(() =>
  import('@/components/PodcastStorySection').then((m) => ({ default: m.PodcastStorySection }))
);
const PodcastGenerator = lazy(() => import('@/components/PodcastGenerator'));

const BelowFoldFallback = () => (
  <div
    className="rounded-2xl border border-border/40 bg-card p-8 animate-pulse space-y-3"
    aria-hidden
  >
    <div className="h-5 w-40 bg-muted rounded-md" />
    <div className="h-4 bg-muted rounded-md w-full" />
    <div className="h-4 bg-muted rounded-md w-5/6" />
    <div className="h-4 bg-muted rounded-md w-4/6" />
  </div>
);
const PlaceDetails = () => {
  const {
    placeName
  } = useParams<{
    placeName: string;
  }>();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    language,
    t,
    translateContinent
  } = useTranslation();
  const {
    toggleFavorite,
    isFavorite
  } = useFavorites();
  const {
    isAuthenticated
  } = useAuth();
  const queryClient = useQueryClient();
  const {
    data: place,
    isPending: loading,
    isError,
    error,
  } = usePlaceDetailQuery(placeName);
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [showPodcastGenerator, setShowPodcastGenerator] = useState(false);
  const [searchTextForPodcast, setSearchTextForPodcast] = useState('');
  const [preselectedLanguage, setPreselectedLanguage] = useState<'fr' | 'en'>('fr');
  const [showImageModal, setShowImageModal] = useState(false);
  // The bottom audio bar (Spotify-style) is always shown when the place has
  // audio, so users can never "lose" the player. Only the language is
  // controlled here so the in-page UI can be in sync with what's being played.
  const [playerLanguage, setPlayerLanguage] = useState<'FR' | 'EN'>(language);
  const [descExpanded, setDescExpanded] = useState(false);
  // Imperative ref so the hero "Listen" pill can start playing AND expand
  // synchronously inside the user-gesture event (autoplay-friendly).
  const playerRef = useRef<PodcastPlayerHandle | null>(null);
  const { navigateBack } = useNavigationState();

  // Keep playback language in sync with UI language by default; user can
  // override from the expanded player.
  useEffect(() => {
    setPlayerLanguage(language);
  }, [language]);
  useEffect(() => {
    if (!placeName) return;
    const decodedPlaceName = decodeURIComponent(placeName);
    document.title =
      language === 'FR'
        ? `${decodedPlaceName} | DiskOver`
        : `${decodedPlaceName} | DiskOver`;
  }, [placeName, language]);

  useEffect(() => {
    if (!isError || !error) return;
    const msg = error instanceof Error ? error.message : '';
    if (msg === 'PLACE_NOT_FOUND') {
      toast({
        title: t('placeNotFound'),
        description: t('placeNotFoundDesc'),
        variant: 'destructive',
      });
      navigate('/');
      return;
    }
    toast({
      title: t('errorTitle'),
      description: t('loadingPlaceError'),
      variant: 'destructive',
    });
    navigate('/');
  }, [isError, error, navigate, toast, t]);

  // SEO optimization - Update title and meta description when place data loads
  useEffect(() => {
    if (place) {
      document.documentElement.setAttribute('lang', language === 'FR' ? 'fr' : 'en');
      // Optimize page title with location and keywords (50-60 chars)
      const titleText = language === 'FR' 
        ? `${place.Name}, ${place.City}: Histoire en Podcast - DiskOver`
        : `${place.Name}, ${place.City}: History and Audio Podcast - DiskOver`;
      document.title = titleText.length > 60 
        ? titleText.substring(0, 57) + '...' 
        : titleText;
      
      // Get place description based on language
      const placeDescription = language === 'FR' 
        ? (place.Description_FR || place.Conseils_visite || place.Informations_clés)
        : (place.Description_EN || place.Description_FR || place.Conseils_visite || place.Informations_clés);
      
      // Create optimized meta description (150-160 chars) with place name and CTA
      let metaDescriptionContent;
      
      if (placeDescription && placeDescription.length > 40) {
        // Use place description with CTA
        const descriptionPrefix = placeDescription.substring(0, 110);
        metaDescriptionContent = language === 'FR'
          ? `${descriptionPrefix}... Podcast 3 min, infos pratiques et conseils de visite.`
          : `${descriptionPrefix}... 3-min podcast, practical info and visit tips.`;
      } else {
        // Fallback with specific structure
        metaDescriptionContent = language === 'FR'
          ? `Découvrez l'histoire de ${place.Name} à travers un podcast immersif de 3 minutes. Infos pratiques, activités et conseils pour préparer votre visite.`
          : `Discover the history of ${place.Name} through a 3-minute immersive podcast. Practical info, activities and visit tips to prepare your discovery.`;
      }
      
      // Ensure description is within 150-160 chars
      if (metaDescriptionContent.length > 160) {
        metaDescriptionContent = metaDescriptionContent.substring(0, 157) + '...';
      }
      
      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', metaDescriptionContent);
      
      // Update canonical link to point to this page's own URL
      const placePath = `/place/${encodeURIComponent(place.Name)}`;
      const canonicalUrl = getCanonicalUrl(placePath);
      
      let linkCanonical = document.querySelector('link[rel="canonical"]');
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonicalUrl);
      
      // Add hreflang links for multilingual support
      addHreflangLinks(placePath);
      
      // Update Open Graph metadata with place-specific content
      const ogTitle = language === 'FR'
        ? `${place.Name}: Découvrez son Histoire en Podcast`
        : `${place.Name}: Discover its History in Podcast`;
      
      const ogDescription = language === 'FR'
        ? `Explorez ${place.Name} avec un podcast culturel immersif. Infos pratiques et conseils de visite.`
        : `Explore ${place.Name} with an immersive cultural podcast. Practical info and visit tips.`;
      
      updateOpenGraphMeta('og:title', ogTitle);
      updateOpenGraphMeta('og:description', ogDescription);
      updateOpenGraphMeta('og:url', canonicalUrl);
      updateOpenGraphMeta('og:type', 'article');
      
      // Use real place image, not logo
      if (place.Image) {
        updateOpenGraphMeta('og:image', place.Image);
        updateOpenGraphMeta('og:image:alt', `${place.Name} - ${place.City}, ${place.Country}`);
      } else {
        updateOpenGraphMeta('og:image', 'https://www.diskover.ovh/og-image.jpg');
        updateOpenGraphMeta('og:image:alt', 'DiskOver - Cultural Podcasts');
      }
      
      // Update Twitter metadata
      updateOpenGraphMeta('twitter:card', 'summary_large_image');
      updateOpenGraphMeta('twitter:title', ogTitle);
      updateOpenGraphMeta('twitter:description', ogDescription);
      
      if (place.Image) {
        updateOpenGraphMeta('twitter:image', place.Image);
        updateOpenGraphMeta('twitter:image:alt', `${place.Name} - ${place.City}, ${place.Country}`);
      } else {
        updateOpenGraphMeta('twitter:image', 'https://www.diskover.ovh/og-image.jpg');
        updateOpenGraphMeta('twitter:image:alt', 'DiskOver - Cultural Podcasts');
      }
      
      // Add structured data (JSON-LD) for the place
      addStructuredData(place, canonicalUrl);
      
      // Add data-snap-ready attribute for react-snap
      document.body.setAttribute('data-snap-ready', 'true');
    }
  }, [place, language]);

  /** Hint the browser to fetch the hero image early (LCP) once we know the URL. */
  useEffect(() => {
    if (!place?.Image) return;
    const href = String(place.Image);
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [place?.Image]);

  // Helper function to update Open Graph and Twitter meta tags
  const updateOpenGraphMeta = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`) || 
               document.querySelector(`meta[name="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      if (property.startsWith('twitter:')) {
        meta.setAttribute('name', property);
      } else {
        meta.setAttribute('property', property);
      }
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  // Helper function to add structured data (JSON-LD) for SEO
  const addStructuredData = (place: PlaceData, canonicalUrl: string) => {
    // Remove existing structured data for places
    const existingScript = document.querySelector('script[data-place-structured-data]');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "TouristAttraction",
      "name": place.Name,
      "description": place.Description_FR || place.Description_EN,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": place.City,
        "addressCountry": place.Country,
        "streetAddress": place.Adresse
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": place.Latitude,
        "longitude": place.Longitude
      },
      "url": canonicalUrl,
      "image": place.Image || 'https://www.diskover.ovh/logo.png',
      "telephone": place.Téléphone ? String(place.Téléphone) : undefined,
      "openingHours": place.Heures || place["Heure d'ouverture"],
      "priceRange": place.Prix || place["Prix du ticket"],
      "additionalType": place.Type_Google || place.Type_lieu,
      "sameAs": place.Site_officiel || place["Site officiel"]
    };

    // Remove undefined values
    Object.keys(structuredData).forEach(key => {
      if (structuredData[key as keyof typeof structuredData] === undefined) {
        delete structuredData[key as keyof typeof structuredData];
      }
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-place-structured-data', 'true');
    script.textContent = JSON.stringify(structuredData, null, 2);
    document.head.appendChild(script);
  };
  const handleScrapePlaceInfo = async () => {
    if (!place?.["Site officiel"]) {
      toast({
        title: t('missingOfficialSite'),
        description: t('missingOfficialSiteDesc'),
        variant: "destructive"
      });
      return;
    }
    setScrapingLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('scrape-place-info', {
        body: {
          placeName: place.Name,
          officialWebsite: place["Site officiel"]
        }
      });
      if (error) throw error;

      // Refresh place data
      const {
        data: updatedData,
        error: fetchError
      } = await supabase.from('podcasts').select('*').eq('Name', place.Name).single();
      if (fetchError) throw fetchError;
      queryClient.setQueryData(placeDetailQueryKey(updatedData.Name), updatedData as PlaceData);
      toast({
        title: t('infoUpdated'),
        description: t('infoUpdatedDesc')
      });
    } catch (error) {
      console.error('Error scraping place info:', error);
      toast({
        title: t('errorTitle'),
        description: t('scrapingError'),
        variant: "destructive"
      });
    } finally {
      setScrapingLoading(false);
    }
  };

  // Handle opening podcast generator
  const handleOpenPodcastGenerator = (locationName: string, language: 'fr' | 'en') => {
    setSearchTextForPodcast(locationName);
    setPreselectedLanguage(language);
    setShowPodcastGenerator(true);
  };

  // Handle podcast generation success
  const handlePodcastGenerated = async () => {
    setShowPodcastGenerator(false);
    setSearchTextForPodcast('');
    setPreselectedLanguage('fr');
    
    // Refresh place data to get the new podcast
    if (place) {
      try {
        const { data: updatedData, error } = await supabase
          .from('podcasts')
          .select('*')
          .eq('Name', place.Name)
          .single();
        
        if (error) throw error;
        queryClient.setQueryData(placeDetailQueryKey(updatedData.Name), updatedData as PlaceData);
        
        toast({
          title: t('podcastCreated'),
          description: t('podcastCreatedDesc'),
        });
      } catch (error) {
        console.error('Error refreshing place data:', error);
      }
    }
  };
  if (loading) {
    return <PlaceDetailSkeleton onBack={() => navigateBack()} />;
  }
  if (isError || !place) {
    return null;
  }

  // Combine old and new field names for compatibility with language support
  const openingHours = place.Heures || place["Heure d'ouverture"];
  const pricing = language === 'EN' && place.Prix_en 
    ? place.Prix_en 
    : (place.Prix || place["Prix du ticket"]);
  const officialSite = place.Site_officiel || place["Site officiel"];
  const keyInfo = place.Informations_clés || place["Informations clés"];
  const visitTips = language === 'EN' && place.Conseils_visite_en 
    ? place.Conseils_visite_en 
    : place.Conseils_visite;
  const placeType = place.Type_Google || place.Type_lieu;
  const phoneNumber = place.Téléphone;
  const address = place.Adresse;
  const rating = place.Note_Google;
  const openingStatus = place.État_ouverture;
  const hasDetailedInfo = openingHours || pricing || keyInfo || visitTips || officialSite;
  const hasPodcastAudio = !!(place.AudioFile_FR || place.AudioFile_EN);
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background/95 to-muted/20 overflow-x-hidden">
      {/* Hero Header */}
      <div className="relative w-full">
        {/* Hero Image */}
        <div className="h-56 sm:h-64 md:h-72 w-full overflow-hidden relative cursor-pointer" onClick={() => place.Image && setShowImageModal(true)}>
          {place.Image ? <OptimizedImage src={place.Image} alt={`${place.Name}, ${place.City}`} className="w-full h-full hover:scale-105 transition-transform duration-300" imgClassName="w-full h-full object-cover object-center" width={1200} height={288} loading="eager" fetchPriority="high" /> : <div className="w-full h-full bg-gradient-earth flex items-center justify-center">
              <MapPin className="w-16 h-16 sm:w-20 sm:h-20 text-white/80" />
            </div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          {place.Image && (
            <div className="absolute top-4 right-4 bg-black/20 backdrop-blur-sm rounded-full p-2 opacity-0 hover:opacity-100 transition-opacity duration-200">
              <ExternalLink className="w-4 h-4 text-white" />
            </div>
          )}
        </div>

        {/* Floating Navigation */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-40">
          <Button
            variant="glass"
            size="icon"
            onClick={() => navigateBack()}
            aria-label={language === 'FR' ? 'Retour' : 'Back'}
            className="w-10 h-10 rounded-full"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Button 
              size="icon" 
              variant="glass" 
              className={`w-10 h-10 rounded-full transition-all duration-200 ${
                isFavorite(place.Name) 
                  ? 'bg-red-500/20 text-red-600 border-red-500/40 hover:bg-red-500/25' 
                  : 'hover:bg-foreground/20'
              }`} 
              onClick={() => {
                if (!isAuthenticated) {
                  navigate('/auth');
                  return;
                }
                toggleFavorite(place.Name);
              }}
              aria-label={isFavorite(place.Name) ? `Retirer ${place.Name} des favoris` : `Ajouter ${place.Name} aux favoris`}
            >
              <Heart className={`w-4 h-4 ${isFavorite(place.Name) ? 'fill-current' : ''}`} />
            </Button>
            <PodcastMenuButton 
              podcastName={place.Name}
              className="bg-foreground/10 text-foreground border border-border/40 backdrop-blur-md hover:bg-foreground/15 w-10 h-10 rounded-full"
            />
          </div>
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white w-full">
          <div className="w-full">
            {/* Audio identity pill - main entry point to listening */}
            {hasPodcastAudio && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  playerRef.current?.playAndExpand();
                }}
                className="group inline-flex items-center gap-2 mb-3 pl-1 pr-3.5 py-1 rounded-full bg-primary text-primary-foreground text-xs sm:text-sm font-bold shadow-lg hover:shadow-xl hover:scale-[1.03] active:scale-95 transition-all"
                aria-label={language === 'FR' ? 'Écouter le podcast' : 'Listen to the podcast'}
              >
                <span className="relative flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white text-primary">
                  <span className="absolute inset-0 rounded-full bg-white/60 animate-ping" aria-hidden="true" />
                  <Play className="relative w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current ml-0.5" />
                </span>
                <Headphones className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="uppercase tracking-wide">
                  {language === 'FR' ? 'Écouter · 3 min' : 'Listen · 3 min'}
                </span>
              </button>
            )}

            <div className="mb-2">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 drop-shadow-lg leading-tight">
                {place.Name}
              </h1>
              <div className="flex flex-col gap-1 text-sm sm:text-base">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{place.City}, {place.Country}</span>
                </div>
                {rating && <div className="flex items-center"><RatingDisplay rating={rating} /></div>}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-1 sm:gap-1.5">
              {placeType && !placeType.startsWith('Favorites, 0ah') && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Badge 
                      variant="secondary" 
                      className="bg-white/20 text-white border-white/30 text-xs px-2 py-0.5 cursor-pointer hover:bg-white/30 transition-all duration-200 hover:scale-105 active:scale-95"
                      title="Cliquez pour voir le type complet"
                    >
                      <Building2 className="w-2.5 h-2.5 mr-1" />
                      <span className="truncate max-w-24">{placeType}</span>
                    </Badge>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="z-50 max-w-sm p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl rounded-lg backdrop-blur-sm" 
                    side="top"
                    sideOffset={8}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-primary" />
                        <p className="text-sm font-semibold text-foreground">Type de lieu</p>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed break-words">
                        {placeType}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 text-xs px-2 py-0.5">
                {translateContinent(place.Continent)}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main
        className="w-full px-3 py-4 sm:px-4 sm:py-6 -mt-6 sm:-mt-8 relative z-10"
        style={{
          // Reserve space for the always-visible mini-player + mobile bottom nav
          paddingBottom: hasPodcastAudio
            ? 'calc(var(--mobile-nav-height, 0px) + 110px)'
            : undefined,
        }}
      >
        <div className="w-full max-w-none space-y-4 sm:space-y-6">
          {/* 1. Audio creation prompt - only visible when no podcast yet.
                Once audio exists, the bottom bar is the sole listening entry point. */}
          {!hasPodcastAudio && (
            <PlaceAudioSection
              place={place}
              language={language}
              activeLanguage={null}
              onPlayPodcast={() => undefined}
              onOpenPodcastGenerator={handleOpenPodcastGenerator}
            />
          )}

          {/* 2. Top grid: main content (left) + compact sidebar (right) */}
          <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Description */}
              <PlaceDetailCard title={t('aboutTitle')}>
                {(() => {
                  const fullDesc =
                    (language === 'FR' ? place.Description_FR : place.Description_EN) ||
                    place.Description_FR ||
                    '';
                  const TRUNCATE_AT = 280;
                  const needsTruncate = fullDesc.length > TRUNCATE_AT;
                  const visible =
                    !needsTruncate || descExpanded
                      ? fullDesc
                      : `${fullDesc.slice(0, TRUNCATE_AT).trimEnd()}…`;
                  return (
                    <>
                      <p className="text-foreground/80 leading-relaxed whitespace-pre-line">
                        {visible}
                      </p>
                      {needsTruncate && (
                        <button
                          type="button"
                          onClick={() => setDescExpanded((v) => !v)}
                          className="mt-3 text-sm font-semibold text-foreground underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground transition-colors"
                        >
                          {descExpanded
                            ? language === 'FR'
                              ? 'Réduire'
                              : 'Show less'
                            : language === 'FR'
                              ? 'Lire la suite'
                              : 'Read more'}
                        </button>
                      )}
                    </>
                  );
                })()}
              </PlaceDetailCard>

              {/* Practical Information - clean rows */}
              {hasDetailedInfo && (openingHours || pricing) && (
                <PlaceDetailCard title={t('practicalInfoTitle')}>
                  <dl className="divide-y divide-border/60">
                    {openingHours && (
                      <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-3 first:pt-0 last:pb-0">
                        <dt className="font-medium text-foreground sm:w-32 flex-shrink-0">
                          {t('scheduleLabel')}
                        </dt>
                        <dd className="text-foreground/75 leading-relaxed flex-1">
                          {openingHours}
                        </dd>
                      </div>
                    )}
                    {pricing && (
                      <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-3 first:pt-0 last:pb-0">
                        <dt className="font-medium text-foreground sm:w-32 flex-shrink-0">
                          {t('pricingLabel')}
                        </dt>
                        <dd className="text-foreground/75 leading-relaxed flex-1">
                          {pricing}
                        </dd>
                      </div>
                    )}
                  </dl>
                </PlaceDetailCard>
              )}

              {/* No detailed info fallback */}
              {!hasDetailedInfo && (
                <PlaceDetailCard title={t('infoCollectionTitle')}>
                  <p className="text-foreground/70 mb-3">{t('infoCollectionDesc')}</p>
                  {place["Site officiel"] || place.Site_officiel ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleScrapePlaceInfo}
                      disabled={scrapingLoading}
                    >
                      <RefreshCw className={`w-3 h-3 mr-2 ${scrapingLoading ? 'animate-spin' : ''}`} />
                      <span className="text-xs">{t('refreshInfoButton')}</span>
                    </Button>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t('noOfficialSite')}</p>
                  )}
                </PlaceDetailCard>
              )}
            </div>

            {/* Right column - Quick contact + Nearby (desktop only, sticky) */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-4 space-y-4">
                {/* Compact contact list - replaces the bubbly Quick Actions */}
                {(officialSite || address || phoneNumber) && (
                  <section className="bg-card rounded-2xl border border-border/50 p-5">
                    <h3 className="text-base font-semibold mb-3 tracking-tight">
                      {language === 'FR' ? 'Infos pratiques' : 'Useful info'}
                    </h3>
                    <ul className="divide-y divide-border/50 -mx-2">
                      {officialSite && (
                        <li>
                          <button
                            type="button"
                            onClick={() => window.open(officialSite, '_blank', 'noopener,noreferrer')}
                            className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
                          >
                            <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground leading-tight">
                                {t('siteLabel')}
                              </p>
                              <p className="text-sm font-medium text-foreground truncate">
                                {officialSite.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                              </p>
                            </div>
                          </button>
                        </li>
                      )}
                      {address && (
                        <li>
                          <button
                            type="button"
                            onClick={() => {
                              const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
                              window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                            }}
                            className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
                          >
                            <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground leading-tight">
                                {t('addressLabel')}
                              </p>
                              <p className="text-sm font-medium text-foreground truncate">
                                {address}
                              </p>
                            </div>
                          </button>
                        </li>
                      )}
                      {phoneNumber && (
                        <li>
                          <button
                            type="button"
                            onClick={() => window.open(`tel:${phoneNumber}`, '_self')}
                            className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
                          >
                            <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground leading-tight">
                                {t('phoneLabel')}
                              </p>
                              <p className="text-sm font-medium text-foreground truncate">
                                {phoneNumber}
                              </p>
                            </div>
                          </button>
                        </li>
                      )}
                    </ul>
                  </section>
                )}

                <NearbyPlaces
                  currentPlace={{
                    Name: place.Name,
                    Latitude: place.Latitude,
                    Longitude: place.Longitude,
                  }}
                />
              </div>
            </aside>
          </div>

          {/* 3. Mobile-only compact contact card (since the desktop sidebar isn't shown) */}
          {(officialSite || address || phoneNumber) && (
            <section className="lg:hidden bg-card rounded-2xl border border-border/50 p-4 sm:p-5">
              <h3 className="text-base font-semibold mb-3 tracking-tight">
                {language === 'FR' ? 'Infos pratiques' : 'Useful info'}
              </h3>
              <ul className="divide-y divide-border/50 -mx-2">
                {officialSite && (
                  <li>
                    <button
                      type="button"
                      onClick={() => window.open(officialSite, '_blank', 'noopener,noreferrer')}
                      className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
                    >
                      <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground leading-tight">{t('siteLabel')}</p>
                        <p className="text-sm font-medium text-foreground truncate">
                          {officialSite.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </p>
                      </div>
                    </button>
                  </li>
                )}
                {address && (
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
                        window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                      }}
                      className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
                    >
                      <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground leading-tight">{t('addressLabel')}</p>
                        <p className="text-sm font-medium text-foreground truncate">{address}</p>
                      </div>
                    </button>
                  </li>
                )}
                {phoneNumber && (
                  <li>
                    <button
                      type="button"
                      onClick={() => window.open(`tel:${phoneNumber}`, '_self')}
                      className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-muted/50 transition-colors text-left"
                    >
                      <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground leading-tight">{t('phoneLabel')}</p>
                        <p className="text-sm font-medium text-foreground truncate">{phoneNumber}</p>
                      </div>
                    </button>
                  </li>
                )}
              </ul>
            </section>
          )}

          {/* 4. Editorial Visit Tips - FULL WIDTH on desktop */}
          {visitTips && (
            <PlaceDetailCard title={t('visitTipsTitle')}>
              <div className="text-foreground/80 whitespace-pre-line leading-relaxed">
                {visitTips}
              </div>
            </PlaceDetailCard>
          )}

          {/* 5. Community Reviews / Tips - Google Maps style, full width */}
          <Suspense fallback={<BelowFoldFallback />}>
            <VisitTipsSection placeName={place.Name} />
          </Suspense>

          {/* 6. Nearby places (mobile only, full width) */}
          <div className="lg:hidden">
            <NearbyPlaces
              currentPlace={{
                Name: place.Name,
                Latitude: place.Latitude,
                Longitude: place.Longitude,
              }}
            />
          </div>

          {/* 7. Podcast story (SEO enrichment) */}
          <Suspense fallback={<BelowFoldFallback />}>
            <PodcastStorySection
              placeName={place.Name}
              script={language === 'FR' ? (place.Podcast_Script_fr || '') : (place.Podcast_Script_en || place.Podcast_Script_fr || '')}
              language={language}
            />
          </Suspense>
        </div>
      </main>

      {/* Image Modal */}
      {place.Image && (
        <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] p-0 border-0 bg-transparent">
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="relative max-w-full max-h-[80vh]">
                <OptimizedImage
                  src={place.Image}
                  alt={`${place.Name}, ${place.City}`}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  width={1200}
                  height={800}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowImageModal(false)}
                  className="absolute top-4 right-4 bg-black/20 hover:bg-black/40 text-white border-0 backdrop-blur-sm rounded-full w-8 h-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Podcast Generator Modal — loaded only when opened to keep initial JS small */}
      {showPodcastGenerator && (
        <Suspense fallback={null}>
          <PodcastGenerator
            isOpen={showPodcastGenerator}
            onClose={() => {
              setShowPodcastGenerator(false);
              setSearchTextForPodcast('');
              setPreselectedLanguage('fr');
            }}
            onPodcastGenerated={handlePodcastGenerated}
            initialSearchText={searchTextForPodcast}
            preselectedLanguage={preselectedLanguage}
          />
        </Suspense>
      )}

      {/* Spotify-style podcast player (mini bar + expandable full screen).
          Always rendered when audio is available so it is the single, always-
          visible listening entry point on the page. */}
      {hasPodcastAudio && (
        <PodcastPlayer
          ref={playerRef}
          podcast={place}
          language={playerLanguage}
          uiLanguage={language}
          onLanguageChange={setPlayerLanguage}
        />
      )}
    </div>
  );
};
export default PlaceDetails;